import type { BoardProject, BoardTech } from '@/hooks/use-staffing-board';

export type CardReadinessSeverity = 'blocked' | 'warning';

export interface CardReadinessReason {
  key: string;
  severity: CardReadinessSeverity;
  message: string;
}

export type CardReadinessStatus = 'ready' | 'warning' | 'blocked' | 'dispatched';

export interface CardReadinessOptions {
  safetyEnabled?: boolean;
  inventoryEnabled?: boolean;
  crewEnabled?: boolean;
  includeDispatchedProjectReasons?: boolean;
}

const OFFBOARDED_STATUSES = new Set(['terminated', 'left']);

function techName(tech: BoardTech): string {
  return `${tech.first_name} ${tech.last_name}`.trim();
}

function expiredCertificationReasons(techs: BoardTech[]): CardReadinessReason[] {
  return techs.flatMap((tech) => {
    if (!tech.has_expired_certs) return [];

    const expiredCerts = (tech.certifications ?? []).filter((cert) => cert.status === 'expired');
    if (expiredCerts.length === 0) {
      return [{
        key: `expired-cert-${tech.id}`,
        severity: 'blocked' as const,
        message: `Expired cert: ${techName(tech)}`,
      }];
    }

    return expiredCerts.map((cert) => ({
      key: `expired-cert-${tech.id}-${cert.id}`,
      severity: 'blocked' as const,
      message: `Expired cert: ${techName(tech)} — ${cert.cert_name || cert.cert_type}`,
    }));
  });
}

function jsaReason(project: BoardProject): CardReadinessReason | null {
  if ((project.jsa_status_color ?? 'red') === 'green') return null;

  const signatureMatch = project.jsa_signature_count?.match(/^(\d+)\s*\/\s*(\d+)/);
  if (project.jsa_status_color === 'yellow' || project.jsa_label === 'JSA unsigned') {
    return {
      key: 'jsa-unsigned',
      severity: 'warning',
      message: signatureMatch
        ? `JSA unsigned — ${signatureMatch[1]} of ${signatureMatch[2]} signatures`
        : 'JSA pending signatures',
    };
  }

  return {
    key: 'jsa-missing',
    severity: 'warning',
    message: 'No JSA for today',
  };
}

// Gear warning checks that also work for callers without H3Gear access,
// who get the yes/no flag and a null count.
export function hasGearInspectionDue(project: BoardProject): boolean {
  return (project.gear_inspection_due ?? 0) > 0 || Boolean(project.has_gear_inspection_due);
}

export function hasGearFailed(project: BoardProject): boolean {
  return (project.gear_failed_count ?? 0) > 0 || Boolean(project.has_gear_failed);
}

export function hasGearLifeExceeded(project: BoardProject): boolean {
  return (project.gear_life_exceeded_count ?? 0) > 0 || Boolean(project.has_gear_life_exceeded);
}

function itemCount(count: number): string {
  return `${count} item${count === 1 ? '' : 's'}`;
}

function pendingGearRequestCount(project: BoardProject): number {
  const summary = project.gear_request_summary;
  if (!summary) return 0;
  return summary.requested
    + summary.fulfilled
    + summary.in_transit
    + (summary.partially_received ?? 0)
    + (summary.pending_confirmation ?? 0);
}

export function getCardReadinessReasons(
  project: BoardProject,
  techs: BoardTech[],
  opts: CardReadinessOptions = {},
): CardReadinessReason[] {
  if (project.last_dispatched_at && !opts.includeDispatchedProjectReasons) return [];

  const crew = techs.filter((tech) => !OFFBOARDED_STATUSES.has(tech.status));
  const reasons = expiredCertificationReasons(crew);

  // Callers without access to a module get its yes/no flag and a null count,
  // so these warnings show without the number (owner ruling, SEC-S9 follow-up).
  const failedCount = project.gear_failed_count ?? 0;
  if (opts.inventoryEnabled !== false && (failedCount > 0 || project.has_gear_failed)) {
    reasons.push({
      key: 'gear-failed',
      severity: 'blocked',
      message: failedCount > 0
        ? `Gear failed inspection (${itemCount(failedCount)})`
        : 'Gear failed inspection',
    });
  }

  const lifeExceededCount = project.gear_life_exceeded_count ?? 0;
  if (
    opts.inventoryEnabled !== false
    && (lifeExceededCount > 0 || project.has_gear_life_exceeded)
  ) {
    reasons.push({
      key: 'gear-life-exceeded',
      severity: 'blocked',
      message: lifeExceededCount > 0
        ? `Gear past service life (${itemCount(lifeExceededCount)})`
        : 'Gear past service life',
    });
  }

  if (opts.safetyEnabled !== false) {
    const jsa = jsaReason(project);
    if (jsa) reasons.push(jsa);

    if (project.jsa_needs_revalidation) {
      reasons.push({
        key: 'jsa-revalidation',
        severity: 'warning',
        message: 'JSA needs revalidation',
      });
    }

    for (const warning of project.crew_plan_initial_warnings ?? []) {
      reasons.push({
        key: `crew-plan-initials-${warning.plan_type}-${warning.plan_revision_id}`,
        severity: 'warning',
        message: warning.message,
      });
    }
  }

  if (
    opts.crewEnabled !== false
    && crew.length > 0
    && !crew.some((tech) => (tech.highest_cert_level ?? 0) >= 3)
  ) {
    reasons.push({
      key: 'no-level-3',
      severity: 'warning',
      message: 'No Level 3 on site',
    });
  }

  if (
    opts.safetyEnabled !== false
    && project.jsa_status === 'ok'
    && (Boolean(project.rescue_plan_stale) || project.rescue_current === false)
  ) {
    reasons.push({
      key: 'rescue-plan',
      severity: 'warning',
      message: project.rescue_plan_stale
        ? 'Rescue plan out of date'
        : 'Rescue plan missing or not signed',
    });
  }

  if (opts.inventoryEnabled !== false) {
    const pendingRequests = pendingGearRequestCount(project);
    if (pendingRequests > 0 || project.has_pending_gear_requests) {
      reasons.push({
        key: 'gear-requests-pending',
        severity: 'warning',
        message: pendingRequests > 0
          ? `Gear requests pending (${pendingRequests})`
          : 'Gear requests pending',
      });
    }

    const inspectionsDue = project.gear_inspection_due ?? 0;
    if (inspectionsDue > 0 || project.has_gear_inspection_due) {
      reasons.push({
        key: 'gear-inspections-due',
        severity: 'warning',
        message: inspectionsDue > 0
          ? `Gear inspections due (${inspectionsDue})`
          : 'Gear inspections due',
      });
    }
  }

  return reasons;
}

export function getCardReadinessStatus(
  project: BoardProject,
  reasons: CardReadinessReason[],
): CardReadinessStatus {
  if (project.last_dispatched_at) return 'dispatched';
  if (reasons.some((reason) => reason.severity === 'blocked')) return 'blocked';
  if (reasons.length > 0) return 'warning';
  return 'ready';
}
